import type { ChapterPlan, ChapterPlanSource, StoryBible } from '../types';

export type RoadmapValidationResult =
  | { valid: true }
  | { valid: false; error: string; code: string };

export interface ApprovedRoadmapPackage {
  chapterPlans: ChapterPlan[];
  roadmapApprovedAt: string;
  roadmapSource: ChapterPlanSource;
}

function normalizeRoadmapSource(source?: ChapterPlanSource): ChapterPlanSource {
  if (source === 'AI_ROADMAP') return 'AI_GENERATED';
  return source || 'AI_GENERATED';
}

function planHasContent(plan: ChapterPlan | null | undefined): boolean {
  if (!plan) return false;
  const title = (plan.title || (plan as any).chapterTitle || '').trim();
  const summary = (plan.summary || '').trim();
  const beats = plan.requiredBeats || [];
  const hasTitle = title.length > 0 && !/^chapter\s+\d+$/i.test(title);
  const hasSummary = summary.length > 0 && summary !== 'needs confirmation';
  const hasBeats = beats.some((b) => b && b.trim().length > 0);
  return hasTitle || hasSummary || hasBeats;
}

export function areChapterPlansBlank(plans: ChapterPlan[] | undefined | null): boolean {
  if (!plans || plans.length === 0) return true;
  return !plans.some(planHasContent);
}

export function resolveActiveRoadmapSource(
  storyBible: StoryBible,
  plans: ChapterPlan[] = storyBible.chapterPlans || [],
): ChapterPlanSource {
  if (storyBible.activeRoadmapSource) return normalizeRoadmapSource(storyBible.activeRoadmapSource);
  const planSource = plans.find((plan) => plan.source)?.source;
  if (planSource) return normalizeRoadmapSource(planSource);
  if (storyBible.storyStructure?.mode === 'partial_ideas') return 'PARTIAL_IDEAS';
  return 'AI_GENERATED';
}

/** @deprecated Use resolveActiveRoadmapSource — premadeRoadmap presence is not authoritative provenance. */
export function inferRoadmapSource(storyBible: StoryBible): ChapterPlanSource {
  return resolveActiveRoadmapSource(storyBible);
}

export function normalizeApprovedChapterPlans(
  plans: ChapterPlan[],
  source: ChapterPlanSource,
  approvedAt: string,
): ChapterPlan[] {
  const normalizedSource = normalizeRoadmapSource(source);
  return plans.map((plan, index) => {
    const chapterNumber = plan.chapterNumber || index + 1;
    const title =
      plan.title ||
      (plan as any).chapterTitle ||
      (plan as any).name ||
      (plan as any).heading ||
      `Chapter ${chapterNumber}`;
    const summary = (plan.summary || '').trim() || (plan as any).rawText?.slice(0, 500) || title;
    const requiredBeats = Array.isArray(plan.requiredBeats)
      ? plan.requiredBeats.filter((b) => typeof b === 'string' && b.trim().length > 0)
      : [];

    return {
      ...plan,
      id: plan.id || `plan_${chapterNumber}_${approvedAt}`,
      chapterNumber,
      title,
      summary,
      requiredBeats,
      status: plan.status || 'planned',
      source: normalizeRoadmapSource(plan.source || normalizedSource),
      approvedAt: plan.approvedAt || approvedAt,
      updatedAt: plan.updatedAt || approvedAt,
    };
  });
}

export function validateApprovedRoadmap(
  plans: ChapterPlan[],
  options?: {
    expectedChapterCount?: number;
    suspectEntryIndexes?: number[];
    suspectEntryReasons?: string[];
  },
): RoadmapValidationResult {
  if (!plans.length) {
    return { valid: false, error: 'No chapter plans to approve. Parse or generate a roadmap first.', code: 'EMPTY_ROADMAP' };
  }
  if (areChapterPlansBlank(plans)) {
    return {
      valid: false,
      error: 'Chapter plans are empty placeholders. Edit or re-parse the roadmap before entering the narrative hub.',
      code: 'BLANK_PLACEHOLDERS',
    };
  }
  const missingNumbers = plans.filter((p) => !p.chapterNumber || p.chapterNumber < 1);
  if (missingNumbers.length > 0) {
    return { valid: false, error: 'One or more chapter plans are missing a valid chapter number.', code: 'INVALID_CHAPTER_NUMBER' };
  }
  if (options?.expectedChapterCount && plans.length !== options.expectedChapterCount) {
    const suspectTitles = (options.suspectEntryIndexes || [])
      .map((index) => plans[index]?.title)
      .filter(Boolean)
      .slice(0, 3);
    const suspectSummary = suspectTitles.length
      ? ` Suspect parsed title(s): ${suspectTitles.map((title) => `"${title}"`).join(', ')}.`
      : '';
    return {
      valid: false,
      error: `Expected ${options.expectedChapterCount} chapters but roadmap has ${plans.length}.${suspectSummary}`,
      code: 'CHAPTER_COUNT_MISMATCH',
    };
  }
  return { valid: true };
}

export function buildApprovedRoadmapPackage(
  storyBible: StoryBible,
  plans: ChapterPlan[],
  approvedAt = new Date().toISOString(),
): ApprovedRoadmapPackage {
  const source = resolveActiveRoadmapSource(storyBible, plans);
  const chapterPlans = normalizeApprovedChapterPlans(plans, source, approvedAt);
  const enforceExpectedCount = Boolean(
    storyBible.targetChapterCount &&
    (source === 'USER_ROADMAP' || chapterPlans.every((plan) => plan.source === 'USER_ROADMAP')),
  );
  const validation = validateApprovedRoadmap(
    chapterPlans,
    enforceExpectedCount
      ? { expectedChapterCount: storyBible.targetChapterCount }
      : undefined,
  );
  if (!validation.valid) {
    throw new Error(validation.error);
  }
  return { chapterPlans, roadmapApprovedAt: approvedAt, roadmapSource: source };
}

export function storyBibleWithApprovedRoadmap(
  storyBible: StoryBible,
  pkg: ApprovedRoadmapPackage,
): StoryBible {
  return {
    ...storyBible,
    chapterPlans: pkg.chapterPlans,
    isRoadmapApproved: true,
    isBlueprintApproved: true,
    targetChapterCount: storyBible.targetChapterCount ?? pkg.chapterPlans.length,
    roadmapApprovedAt: pkg.roadmapApprovedAt,
    activeRoadmapSource: pkg.roadmapSource,
    originalUserRoadmapRaw: storyBible.originalUserRoadmapRaw ?? storyBible.premadeRoadmap,
    originalUserRoadmapParsed: storyBible.originalUserRoadmapParsed,
  } as StoryBible;
}

export function verifyPersistedChapterPlans(
  persisted: ChapterPlan[] | undefined,
  expected: ChapterPlan[],
): RoadmapValidationResult {
  if (!persisted?.length) {
    return { valid: false, error: 'Persisted roadmap is missing after save.', code: 'PERSIST_VERIFY_EMPTY' };
  }
  if (persisted.length < expected.length) {
    return {
      valid: false,
      error: `Persisted roadmap lost chapters (${persisted.length}/${expected.length}).`,
      code: 'PERSIST_VERIFY_TRUNCATED',
    };
  }
  const firstExpected = expected[0];
  const firstPersisted = persisted.find((p) => p.chapterNumber === firstExpected.chapterNumber) || persisted[0];
  if (!planHasContent(firstPersisted)) {
    return { valid: false, error: 'Persisted roadmap loaded blank chapter content.', code: 'PERSIST_VERIFY_BLANK' };
  }
  if (firstExpected.title && firstPersisted.title && firstPersisted.title !== firstExpected.title) {
    if (!planHasContent(firstPersisted)) {
      return { valid: false, error: 'Persisted roadmap title/content mismatch.', code: 'PERSIST_VERIFY_MISMATCH' };
    }
  }
  return { valid: true };
}

export function readChapterPlansFromSavedState(state: { storyBible?: StoryBible } | null | undefined): ChapterPlan[] {
  return state?.storyBible?.chapterPlans || [];
}

export function verifyPersistedRoadmapSource(
  persisted: StoryBible | undefined,
  expectedSource: ChapterPlanSource,
): RoadmapValidationResult {
  if (!persisted) {
    return { valid: false, error: 'Persisted story bible missing after save.', code: 'PERSIST_VERIFY_EMPTY' };
  }
  const actual = resolveActiveRoadmapSource(persisted, persisted.chapterPlans || []);
  if (actual !== normalizeRoadmapSource(expectedSource)) {
    return {
      valid: false,
      error: `Persisted roadmap source mismatch (${actual} vs ${expectedSource}).`,
      code: 'PERSIST_VERIFY_SOURCE',
    };
  }
  return { valid: true };
}
